/**
 * ============================================================================
 * [P3-04] Firestore URL Links Service
 *
 * URL 링크(urlLinks) 컬렉션에 대한 CRUD 및 실시간 구독 함수
 *
 * 기능:
 * - createURLLink: URL 링크 생성
 * - updateURLLink: URL 링크 수정
 * - deleteURLLink: URL 링크 삭제
 * - subscribeToURLLinks: 실시간 구독
 *
 * 컬렉션 경로: users/{userId}/urlLinks
 * ============================================================================
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  type CollectionReference,
} from 'firebase/firestore'
import { db } from '../firebase'
import type { URLLink } from '@/types'

// ============================================================================
// 컬렉션 참조 헬퍼
// ============================================================================
const getURLLinksCollection = (userId: string): CollectionReference =>
  collection(db, 'users', userId, 'urlLinks')

// ============================================================================
// Firestore 변환 헬퍼
// ============================================================================
const urlLinkFromFirestore = (doc: { id: string; data: () => Record<string, unknown> }): URLLink => {
  const data = doc.data()
  const createdAtData = data.createdAt as { toDate?: () => Date } | undefined
  return {
    id: doc.id,
    name: (data.name as string) || '',
    title: (data.title as string) || undefined,
    description: (data.description as string) || undefined,
    url: (data.url as string) || '',
    category: (data.category as string) || undefined,
    icon: (data.icon as string) || undefined,
    order: (data.order as number) ?? 0,
    createdAt: createdAtData?.toDate?.() || new Date(),
  }
}

// ============================================================================
// [P3-04-A] URL 링크 생성 (Create)
// ============================================================================
export async function createURLLink(
  userId: string,
  linkData: Omit<URLLink, 'id' | 'createdAt'>
): Promise<URLLink> {
  if (!userId) {
    throw new Error('[createURLLink] userId is required')
  }
  if (!linkData.name?.trim() || !linkData.url?.trim()) {
    throw new Error('[createURLLink] name and url are required')
  }

  const urlLinksRef = getURLLinksCollection(userId)
  
  const docData = {
    name: linkData.name.trim(),
    description: linkData.description?.trim() || null,
    url: linkData.url.trim(),
    category: linkData.category?.trim() || null,
    icon: linkData.icon || null,
    order: linkData.order ?? 0,
    createdAt: serverTimestamp(),
  }

  const docRef = await addDoc(urlLinksRef, docData)
  console.log('[createURLLink] URL 링크 생성 완료:', docRef.id)

  return {
    id: docRef.id,
    name: linkData.name.trim(),
    description: linkData.description?.trim(),
    url: linkData.url.trim(),
    category: linkData.category?.trim(),
    icon: linkData.icon,
    order: linkData.order ?? 0,
    createdAt: new Date(),
  }
}

// ============================================================================
// [P3-04-B] URL 링크 수정 (Update)
// ============================================================================
export async function updateURLLink(
  userId: string,
  linkId: string,
  updates: Partial<URLLink>
): Promise<void> {
  if (!userId) {
    throw new Error('[updateURLLink] userId is required')
  }
  if (!linkId) {
    throw new Error('[updateURLLink] linkId is required')
  }

  const linkRef = doc(db, 'users', userId, 'urlLinks', linkId)
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const safeUpdates: Record<string, any> = {}
  
  if (updates.name !== undefined) safeUpdates.name = updates.name.trim()
  if (updates.description !== undefined) safeUpdates.description = updates.description?.trim() || null
  if (updates.url !== undefined) safeUpdates.url = updates.url.trim()
  if (updates.category !== undefined) safeUpdates.category = updates.category?.trim() || null
  if (updates.icon !== undefined) safeUpdates.icon = updates.icon || null
  if (updates.order !== undefined) safeUpdates.order = updates.order

  if (Object.keys(safeUpdates).length === 0) {
    console.warn('[updateURLLink] No valid updates provided')
    return
  }

  safeUpdates.updatedAt = serverTimestamp()

  await updateDoc(linkRef, safeUpdates)
  console.log('[updateURLLink] URL 링크 수정 완료:', linkId)
}

// ============================================================================
// [P3-04-C] URL 링크 삭제 (Delete)
// ============================================================================
export async function deleteURLLink(
  userId: string,
  linkId: string
): Promise<void> {
  if (!userId) {
    throw new Error('[deleteURLLink] userId is required')
  }
  if (!linkId) {
    throw new Error('[deleteURLLink] linkId is required')
  }

  const linkRef = doc(db, 'users', userId, 'urlLinks', linkId)
  await deleteDoc(linkRef)
  console.log('[deleteURLLink] URL 링크 삭제 완료:', linkId)
}

// ============================================================================
// [P3-04-D] 실시간 구독 (Subscribe)
// - CRITICAL: 반환된 unsubscribe 함수를 반드시 cleanup에서 호출할 것
// ============================================================================
export function subscribeToURLLinks(
  userId: string,
  onData: (links: URLLink[]) => void,
  onError?: (error: Error) => void
): () => void {
  if (!userId) {
    console.warn('[subscribeToURLLinks] userId is null, returning no-op')
    return () => {}
  }

  const urlLinksRef = getURLLinksCollection(userId)
  const q = query(urlLinksRef, orderBy('order', 'asc'), orderBy('createdAt', 'desc'))

  console.log('[subscribeToURLLinks] 실시간 구독 시작:', userId)

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const links = snapshot.docs.map((docSnap) => urlLinkFromFirestore(docSnap))
      console.log('[subscribeToURLLinks] 데이터 수신:', links.length, '개')
      onData(links)
    },
    (error) => {
      console.error('[subscribeToURLLinks] 구독 에러:', error)
      onError?.(error)
    }
  )

  return () => {
    console.log('[subscribeToURLLinks] 구독 해제:', userId)
    unsubscribe()
  }
}
